import util from 'preact-util';

const STAGE_TYPE_LABELS = {
    'nais': 'NAIS',
    'hurtig': 'Hurtig',
    'hurtig-half': 'Hurtig halv',
    'standard': 'Standard',
    'felt': 'Felt',
    'norgesfelt': 'Norgesfelt',
    't-96': 'T-96',
    'finpistol': 'VM-Finpistol',
    'grovpistol': 'VM-Grovpistol',
    'silhuett': 'Silhuettpistol',
    'luftpistol': 'Luftpistol 10m',
    'griseskyting': 'Griseskyting',
    'skarpskytter': 'Skarpskyttermerke',
    'tidsoving': 'Tidsøvelse',
    'tidskonk': 'Tidskonkurranse',
    'fri': 'Fri (poeng)',
};

const WEAPON_LABELS = {
    'fin': 'Finfelt',
    'grov': 'Grovfelt',
    'mil': 'Militærfelt',
    'milrp': 'Militærfelt-Rødpunkt',
    'rev': 'Revolverfelt',
    'revrp': 'Revolverfelt-Rødpunkt',
    'sp.rev': 'Spesialrevolver',
    'sp.pistol': 'Spesialpistol',
    'mag1': 'Magnumfelt 1',
    'mag2': 'Magnumfelt 2',
    'luft': 'Luft',
};

// Display order for weapon classes (kanban class filter pills etc.).
export const WEAPON_CLASS_ORDER = Object.keys(WEAPON_LABELS);

const SINGLE_CLASS_WEAPONS = ['sp.pistol', 'sp.rev', 'mag1', 'mag2'];
// Rødpunkt (red-dot) disciplines have a single open class — Klasse Å — with no A–D skill split.
const RODPUNKT_WEAPONS = ['milrp', 'revrp'];
const SKILL_CLASS_STAGE_TYPES = ['felt', 'hurtig', 'nais', 't-96'];

// NSF rule: a shooter with no established classification for a field discipline
// competes in the lowest class — Klasse D — not a non-existent "Ingen klasse" bucket.
const NSF_DEFAULT_SKILL_CLASS = 'D';

// A Rubic class object exposes a bare `classCode` (e.g. "A", "D") alongside a
// branch-prefixed `name` ("Pistol\\D", "Rifle/Menn"). Bucket by the bare code so a
// classified "Pistol\\D" shooter and an unclassified shooter (NSF default "D") land
// in the SAME "Klasse D" bucket, and so it matches the admin override (A–D) and
// SKILL_CLASS_ORDER. Falls back to the last path segment of the name when classCode
// is absent (mirrors the backend's rubic-references.js handling).
function resolveClassCode(klass) {
    if (!klass) return '';
    if (klass.classCode) return String(klass.classCode).trim();
    if (klass.name) {
        const segments = String(klass.name).split(/[\\/]/);
        return segments[segments.length - 1].trim();
    }
    return '';
}

export function getStageTypeDisplay(type) {
    if (!type) return type || '';
    return STAGE_TYPE_LABELS[type] || type;
}

// Short "30. mai" label when the stage falls on a different calendar day than the
// convention's start date; empty string on the same day (or when no stage date).
export function getStageDateLabel(stage, convention) {
    const stageDate = stage?.date ? new Date(stage.date) : null;
    if (!stageDate) {
        return '';
    }
    const conventionStart = convention?.startDate ? new Date(convention.startDate) : null;
    const sameDay = conventionStart
        && stageDate.getFullYear() === conventionStart.getFullYear()
        && stageDate.getMonth() === conventionStart.getMonth()
        && stageDate.getDate() === conventionStart.getDate();
    return sameDay ? '' : stageDate.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
}

// Stage time for display, prefixed with the short date on multi-day conventions:
// "07:00" on the convention day, "30. mai 07:00" on a different day.
export function getStageTimeLabel(stage, convention) {
    if (!stage?.date) {
        return '';
    }
    const dateLabel = getStageDateLabel(stage, convention);
    return `${dateLabel ? `${dateLabel} ` : ''}${util.isoTime(stage.date)}`;
}

export function resolveWeaponDisplay(weapon) {
    if (!weapon || !WEAPON_LABELS[weapon]) return 'Ingen klasse';
    return WEAPON_LABELS[weapon];
}

export function resolveSkillClass(participant, currentParticipant, stage, classMap, disciplineMap) {
    const weaponClass = participant?.class || '';
    const stageType = stage?.type || '';

    if (stageType === 'norgesfelt') return null;
    if (SINGLE_CLASS_WEAPONS.includes(weaponClass)) return 'A';
    // Rødpunkt resolves to Klasse Å automatically — including shooters with no
    // established classification (Rødpunkt has no A–D split).
    if (RODPUNKT_WEAPONS.includes(weaponClass)) return 'Å';
    if (!SKILL_CLASS_STAGE_TYPES.includes(stageType)) return 'A';

    // Admin-set skill class wins for the multi-class field disciplines.
    if (participant?.skillClass) return participant.skillClass;

    const disciplineName = resolveWeaponDisplay(weaponClass);
    const discipline = (disciplineMap || []).find(d => d.name === disciplineName);
    if (!discipline) return 'Ingen klasse';

    const ndc = (currentParticipant?.nsfDiciplineClasses || [])
        .find(c => c.disciplineId === discipline.id);
    if (!ndc) return NSF_DEFAULT_SKILL_CLASS;

    const klass = (classMap || []).find(c => c.id === ndc.classId);
    return resolveClassCode(klass) || NSF_DEFAULT_SKILL_CLASS;
}

// A shooter's nsfDiciplineClasses (synced from Rubic) holds one row per
// discipline PER YEAR, so an active shooter accumulates many rows across seasons.
// This collapses them to the single relevant row per discipline: the one whose
// periodYear matches the convention's year, falling back to the most recent year
// when there is no exact match. Used to show each weapon class's current
// classification instead of every historical year at once.
function classRelevanceScore(classItem, conventionYear) {
    const year = classItem.periodYear || 0;
    // An exact convention-year match always wins; otherwise the latest year wins.
    return year === conventionYear ? Number.MAX_SAFE_INTEGER : year;
}

// The year a convention belongs to (from its startDate), used to pick the
// relevant classification per discipline. Falls back to the current year.
export function conventionYearOf(convention) {
    const date = convention && convention.startDate ? new Date(convention.startDate) : new Date();
    const year = date.getFullYear();
    return Number.isNaN(year) ? new Date().getFullYear() : year;
}

export function pickCurrentDisciplineClasses(classes, conventionYear) {
    const byDiscipline = new Map();
    (classes || []).forEach((classItem) => {
        if (!classItem || classItem.disciplineId == null) {
            return;
        }
        const existing = byDiscipline.get(classItem.disciplineId);
        if (!existing
            || classRelevanceScore(classItem, conventionYear) > classRelevanceScore(existing, conventionYear)) {
            byDiscipline.set(classItem.disciplineId, classItem);
        }
    });
    return Array.from(byDiscipline.values());
}

const WEAPON_ABBR = {
    'fin': 'F',
    'grov': 'G',
    'mil': 'M',
    'milrp': 'MR',
    'rev': 'R',
    'revrp': 'RR',
    'sp.pistol': 'SP',
    'sp.rev': 'SR',
    'mag1': 'M1',
    'mag2': 'M2',
    'luft': 'L',
};

export function weaponAbbr(weapon) {
    return WEAPON_ABBR[weapon] || '';
}

// True when at least one of a shooter's result rows on a Lag was written by the
// Megalink pipeline (scoreSource 'megalink'): the convention page then offers
// the shot-by-shot drawer for that shooter.
export function hasMegalinkResults(results) {
    return (Array.isArray(results) ? results : []).some(r => r && r.scoreSource === 'megalink');
}

export function formatProgressCell(result, stageType) {
    if (!result) return '';
    const hits = result.scoreHits || 0;
    const targets = result.scoreTargets || 0;
    const inner = result.scoreInner || 0;
    const groups = result.scoreGroups || 0;
    const total = result.scoreTotal;

    if (stageType === 't-96') {
        return String(hits + targets + inner);
    }
    if (stageType === 'tidsoving' || stageType === 'tidskonk') {
        if (total == null) return '';
        return `${parseFloat(total).toFixed(2)}s`;
    }
    const base = `${hits}/${targets} (${inner})`;
    if (stageType === 'norgesfelt' && groups > 0) {
        return `${base} g${groups}`;
    }
    return base;
}

export function expectedStandsForStage(stage, convention) {
    if (!stage) return 1;
    const type = stage.type;
    if (type === 'norgesfelt') return 8;
    if (type === 't-96') return 16;
    if (type === 'tidskonk') return 3;
    if (type === 'felt') return stage.stages || convention?.stageStands?.length || 10;
    return stage.stages || convention?.stageStands?.length || 1;
}

/**
 * Returns per-stand max score caps for input validation.
 * For felt, fields are uncapped (null) unless the convention has explicit stageStands
 * — felt has no predefined score limits, so any number of hits/targets/inners may apply
 * depending on the stand layout chosen by the organizer.
 * Other types fall back to 6/6/6 (the historical default).
 */
export function getStandMaxValues(stage, stand, convention) {
    const stageStands = convention?.stageStands || [];
    const standNum = parseInt(stand, 10);
    const explicit = stageStands.find(item => parseInt(item.stand, 10) === standNum);
    const fromExplicit = (key) => {
        const v = parseInt(explicit?.[key], 10);
        return Number.isFinite(v) && v > 0 ? v : null;
    };
    const type = stage?.type;
    if (type === 'felt') {
        return {
            scoreHits: fromExplicit('scoreHits'),
            scoreTargets: fromExplicit('scoreTargets'),
            scoreInner: fromExplicit('scoreInner'),
        };
    }
    return {
        scoreHits: fromExplicit('scoreHits') ?? 6,
        scoreTargets: fromExplicit('scoreTargets') ?? 6,
        scoreInner: fromExplicit('scoreInner') ?? 6,
    };
}

/**
 * Optional per-stand metadata an organizer can set on each standplass:
 * shootingTime (seconds), shootingPosition (free text) and notes (free text).
 * Looked up by stand number from convention.stageStands. Returns null when the
 * stand has no entry or no metadata, so callers can skip rendering entirely.
 */
export function getStandInfo(convention, stand) {
    const stageStands = convention?.stageStands || [];
    const standNum = parseInt(stand, 10);
    const entry = stageStands.find(item => parseInt(item.stand, 10) === standNum);
    if (!entry) return null;
    const time = parseInt(entry.shootingTime, 10);
    const shootingTime = Number.isFinite(time) && time > 0 ? time : null;
    const shootingPosition = (entry.shootingPosition || '').toString().trim() || null;
    const notes = (entry.notes || '').toString().trim() || null;
    if (shootingTime == null && !shootingPosition && !notes) return null;
    return { shootingTime, shootingPosition, notes };
}

/**
 * Per-stand scoring layout caps an organizer set on each standplass: number of
 * figures (scoreTargets), target groups (scoreGroups) and scoring zones (scoreInner).
 * Looked up by stand number from convention.stageStands; each value is a positive
 * integer or null. Returns null when the stand has no such config, so callers can
 * skip rendering entirely.
 */
export function getStandScoreConfig(convention, stand) {
    const stageStands = convention?.stageStands || [];
    const standNum = parseInt(stand, 10);
    const entry = stageStands.find(item => parseInt(item.stand, 10) === standNum);
    if (!entry) return null;
    const toPositive = (value) => {
        const n = parseInt(value, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    };
    const scoreTargets = toPositive(entry.scoreTargets);
    const scoreGroups = toPositive(entry.scoreGroups);
    const scoreInner = toPositive(entry.scoreInner);
    if (scoreTargets == null && scoreGroups == null && scoreInner == null) return null;
    return { scoreTargets, scoreGroups, scoreInner };
}

// Special field disciplines whose per-stand max score is capped at 5 (not 6).
// Ported verbatim from calculateBasedOnClass in
// backend/app/routes/api/integrations/rubic/post-competition-results-report.js —
// keep the two in sync.
const SPECIAL_DISCIPLINES = ['Spesialrevolver', 'Spesialpistol', 'Magnumfelt 1', 'Magnumfelt 2'];

// Weapon-class codes that share a per-convention max config (see resolveStandConfig):
// sp.pistol/sp.rev read convention.stageStandsSpecial; mag1/mag2 read stageStandsMagnum.
const SPECIAL_PISTOL_REV_CLASSES = ['sp.pistol', 'sp.rev'];
// Exported: the kanban uses it for the magnum 2 m spacing warning.
export const MAGNUM_CLASSES = ['mag1', 'mag2'];

export function calculateBasedOnClass(className, value) {
    if (SPECIAL_DISCIPLINES.includes(className)) {
        return Math.min(value, 5);
    }
    return value;
}

// ---------------------------------------------------------------------------
// "% av beste": each ranked sum measured against the best sum shot in the same
// weapon section that day (all skill classes together — Norgesfelt is not
// class-determining, so the winner of the weapon class is the 100 % reference).
// Statused rows (DNF/DNS/DSQ) and time stages (lower is better) are left out.
// ---------------------------------------------------------------------------

export function bestScoreBySection(resultList) {
    const best = {};
    Object.keys(resultList || {}).forEach((key) => {
        const bucket = resultList[key];
        if (!bucket || bucket.isTimeStage || !bucket.sectionKey) return;
        (bucket.participants || []).forEach((p) => {
            if (!p || p.resultStatus) return;
            const score = Number(p.totalScore);
            if (Number.isNaN(score)) return;
            if (!(best[bucket.sectionKey] > score)) best[bucket.sectionKey] = score;
        });
    });
    return best;
}

export function percentOfBest(score, best) {
    if (!(best > 0) || score === '' || score === null || score === undefined) return null;
    const s = Number(score);
    if (Number.isNaN(s)) return null;
    return Math.round((s / best) * 100);
}

// The shooter drawer's one-liner: percent of the open (non-championship) weapon
// section the shooter's start belongs to. null when unranked or on a time stage.
export function shooterPercentOfBest(resultList, participantId, stageMd5) {
    if (!resultList) return null;
    const best = bestScoreBySection(resultList);
    const keys = Object.keys(resultList);
    for (let i = 0; i < keys.length; i += 1) {
        const bucket = resultList[keys[i]];
        if (bucket && !bucket.isTimeStage && !bucket.championship) {
            const p = (bucket.participants || []).find(x => x.id === participantId && x.stageMd5 === stageMd5);
            if (p) {
                if (p.resultStatus) return null;
                const reference = best[bucket.sectionKey];
                const percent = percentOfBest(p.totalScore, reference);
                return percent === null ? null : { percent, best: reference };
            }
        }
    }
    return null;
}

// Per-stand max caps used by the score validator. Mirrors the Rubic report's
// effective max: each field defaults to 6 when the stand has no explicit
// stageStands entry (getStandMaxValues returns null for felt), then is capped to
// 5 for the special disciplines via calculateBasedOnClass.
export function getValidationStandMax(stage, stand, convention, weaponClass) {
    // Explicit per-class config (sp/magnum) wins, used verbatim.
    const explicit = perClassStandEntry(convention, stand, weaponClass);
    if (explicit) {
        return {
            scoreHits: parseInt(explicit.scoreHits, 10) || 0,
            scoreTargets: parseInt(explicit.scoreTargets, 10) || 0,
            scoreInner: parseInt(explicit.scoreInner, 10) || 0,
        };
    }
    const base = getStandMaxValues(stage, stand, convention);
    const disciplineName = resolveWeaponDisplay(weaponClass);
    return {
        scoreHits: calculateBasedOnClass(disciplineName, base.scoreHits ?? 6),
        scoreTargets: calculateBasedOnClass(disciplineName, base.scoreTargets ?? 6),
        scoreInner: calculateBasedOnClass(disciplineName, base.scoreInner ?? 6),
    };
}

// Time stages are scored via scoreTotal, not hits/targets/inner, so no per-stand
// hit caps apply — the validator skips them.
const TIME_STAGE_TYPES = ['tidsoving', 'tidskonk'];
const VALIDATED_SCORE_FIELDS = ['scoreHits', 'scoreTargets', 'scoreInner'];

// Audits every conventionResult row against the per-stand max scores configured on
// the convention (standplass / stageStands), applying the same special-class cap of
// 5 the Rubic report uses. Returns the over-max violations plus how many rows were
// checked and how many could not be matched to a stage. Callers pass results already
// filtered to a single convention.
export function validateConventionScores(convention, conventionResults) {
    const violations = [];
    let checkedCount = 0;
    let unmatchedCount = 0;
    const stages = convention?.stages || [];
    const results = conventionResults || [];

    results.forEach((result) => {
        if (!result) return;
        const stageIdx = stages.findIndex(s => s.md5 === result.stage);
        if (stageIdx < 0) {
            unmatchedCount += 1;
            return;
        }
        const stage = stages[stageIdx];
        if (TIME_STAGE_TYPES.includes(stage.type)) {
            return;
        }
        checkedCount += 1;

        const stageParticipants = convention[`participants${stageIdx + 1}`] || [];
        const sp = stageParticipants.find(p => p.id === result.participant);
        const weaponClass = sp?.class || '';
        const disciplineName = resolveWeaponDisplay(weaponClass);
        const max = getValidationStandMax(stage, result.stand, convention, weaponClass);
        const participantName = sp
            ? (`${sp.firstname || ''} ${sp.lastname || ''}`.trim() || 'Ukjent skytter')
            : 'Ukjent skytter';
        const club = sp?.sportsClub || '';
        const stageName = stage.name || getStageTypeDisplay(stage.type);

        VALIDATED_SCORE_FIELDS.forEach((field) => {
            const value = result[field] || 0;
            const fieldMax = max[field];
            if (value > fieldMax) {
                violations.push({
                    participant: result.participant,
                    participantName,
                    club,
                    stageIdx,
                    stageName,
                    stageType: stage.type,
                    stand: result.stand,
                    weaponClass,
                    disciplineName,
                    field,
                    value,
                    max: fieldMax,
                });
            }
        });
    });

    return { violations, checkedCount, unmatchedCount };
}

export function perStandScore(result, stageType) {
    if (!result) return 0;
    const hits = result.scoreHits || 0;
    const targets = result.scoreTargets || 0;
    const inner = result.scoreInner || 0;
    const penalty = result.scorePenalty || 0;
    const total = result.scoreTotal;

    if (stageType === 't-96') return hits + targets + inner;
    if (stageType === 'tidsoving' || stageType === 'tidskonk') {
        if (total == null) return 0;
        return parseFloat(total);
    }
    return Math.max(0, hits + targets - penalty);
}

/**
 * Sums the per-stand scores of a row's `cells` map into a running row total,
 * matching what the Resultatliste tab shows. For time stages the total is the
 * best (lowest) recorded time rather than a sum.
 */
export function rowTotals(cells, stageType) {
    const list = Object.values(cells || {});
    const isTime = stageType === 'tidsoving' || stageType === 'tidskonk';
    if (isTime) {
        const times = list
            .map(c => (c && c.scoreTotal != null ? parseFloat(c.scoreTotal) : null))
            .filter(t => t != null && !Number.isNaN(t));
        const totalScore = times.length ? Math.min(...times) : null;
        return { totalScore, totalIx: 0, isTime: true };
    }
    const totalScore = list.reduce((sum, c) => sum + perStandScore(c, stageType), 0);
    const totalIx = list.reduce((sum, c) => sum + (c?.scoreInner || 0), 0);
    return { totalScore, totalIx, isTime: false };
}

/**
 * True when a shooter is still on a maximum score "so far": every stand they
 * have already shot (each entry in `cells`) is at its full per-stand max. Used
 * to emphasise full-score shooters in the Pågående (in-progress) list. Stands
 * not yet shot are ignored, so the flag holds mid-round and clears the moment a
 * point is dropped. Only stand-based field shooting and t-96 have a defined max;
 * time stages and uncapped felt stands can't be "perfect" → false.
 * Uses perStandScore + standMaxForCell so the check matches how totals are summed.
 */
export function isPerfectSoFar(cells, stage, convention, weaponClass) {
    const stageType = stage?.type;
    if (!stageType) return false;
    if (stageType === 'tidsoving' || stageType === 'tidskonk') return false;
    const entries = Object.entries(cells || {});
    if (entries.length === 0) return false;
    if (stageType === 't-96') {
        // Full t-96 series = 6 hits + 5 figures + 6 inner = 17 (matches perStandScore for t-96).
        return entries.every(([, c]) => perStandScore(c, stageType) === 17);
    }
    if (stageType !== 'felt' && stageType !== 'norgesfelt') return false;
    return entries.every(([stand, c]) => {
        const max = standMaxForCell(stage, stand, convention, weaponClass);
        const maxSum = (max.scoreHits || 0) + (max.scoreTargets || 0);
        if (maxSum <= 0) return false; // uncapped felt → can't verify "perfect"
        return perStandScore(c, stageType) === maxSum;
    });
}

/**
 * Per-stand score summary for the Pågående cells: the points scored on one stand
 * and how many were dropped from that stand's max. Returns null when there is no
 * result yet, or for stage types without a meaningful per-stand max (time stages,
 * precision) so the caller renders nothing extra. felt/norgesfelt read the
 * per-stand cap (uncapped felt → score only, no loss); t-96 caps at 17 per series.
 */
export function getStandCellInfo(result, stand, stage, convention, weaponClass) {
    if (!result) return null;
    const stageType = stage?.type;
    if (!stageType) return null;
    if (stageType === 't-96') {
        const score = perStandScore(result, stageType); // 6 hits + 5 figures + 6 inner = 17
        return { score, max: 17, lost: Math.max(0, 17 - score) };
    }
    if (stageType !== 'felt' && stageType !== 'norgesfelt') return null;
    const score = perStandScore(result, stageType);
    const max = standMaxForCell(stage, stand, convention, weaponClass);
    const maxSum = (max.scoreHits || 0) + (max.scoreTargets || 0);
    if (maxSum <= 0) return { score, max: null, lost: null }; // uncapped felt → score only
    return { score, max: maxSum, lost: Math.max(0, maxSum - score) };
}

/**
 * Row-level loss summary for the Pågående Sum column: how many points a shooter
 * has dropped so far and the best final score they can still reach.
 *   totalLost   = sum of per-stand lost points over the stands shot
 *   stageMax    = sum of the per-stand max over all `expectedStands`
 *   maxPossible = stageMax − totalLost (equals the score shot once every stand is in)
 * Uses getStandCellInfo per stand so the numbers match the per-stand badges.
 * Returns all-null for stage types without a per-stand max (time, precision) and
 * for any stage with an uncapped stand, so the caller renders nothing extra.
 */
export function rowLossSummary(cells, stage, convention, weaponClass, expectedStands) {
    const empty = { totalLost: null, stageMax: null, maxPossible: null };
    const stageType = stage?.type;
    if (!stageType) return empty;
    if (stageType !== 'felt' && stageType !== 'norgesfelt' && stageType !== 't-96') return empty;

    const stands = parseInt(expectedStands, 10) || 0;
    if (stands <= 0) return empty;

    // Probe each expected stand with an empty result to read its max; stands not
    // yet shot contribute their full max to stageMax and nothing to totalLost.
    const probe = { scoreHits: 0, scoreTargets: 0, scoreInner: 0 };
    let stageMax = 0;
    for (let n = 1; n <= stands; n += 1) {
        const info = getStandCellInfo(probe, n, stage, convention, weaponClass);
        if (!info || info.max == null) return empty; // uncapped stand → no meaningful max
        stageMax += info.max;
    }

    let totalLost = 0;
    const shot = Object.entries(cells || {});
    for (let i = 0; i < shot.length; i += 1) {
        const [stand, cell] = shot[i];
        const info = getStandCellInfo(cell, stand, stage, convention, weaponClass);
        if (!info || info.lost == null) return empty;
        totalLost += info.lost;
    }

    return { totalLost, stageMax, maxPossible: stageMax - totalLost };
}

// ---------------------------------------------------------------------------
// Per-stand analysis (Analyse tab on the convention results page).
// ---------------------------------------------------------------------------

// Stand-based felt stage types eligible for the per-stand analysis. t-96 is
// "field shooting" in the store sense but is target/series-based, and time
// stages don't use the hits/figures model — both are excluded here.
const STAND_BASED_STAGE_TYPES = ['felt', 'norgesfelt'];

// Fixed per-stand max for Norgesfelt. Mirrors
// conventionResult store getNorgesfeltTargetMaxValues() — keep the two in sync.
const NORGESFELT_TARGET_MAX = [
    { stand: 1, scoreHits: 6, scoreGroups: 1, scoreTargets: 2, scoreInner: 6 },
    { stand: 2, scoreHits: 6, scoreGroups: 2, scoreTargets: 6, scoreInner: 6 },
    { stand: 3, scoreHits: 6, scoreGroups: 2, scoreTargets: 5, scoreInner: 6 },
    { stand: 4, scoreHits: 6, scoreGroups: 1, scoreTargets: 3, scoreInner: 6 },
    { stand: 5, scoreHits: 6, scoreGroups: 1, scoreTargets: 2, scoreInner: 6 },
    { stand: 6, scoreHits: 6, scoreGroups: 2, scoreTargets: 5, scoreInner: 6 },
    { stand: 7, scoreHits: 6, scoreGroups: 1, scoreTargets: 3, scoreInner: 6 },
    { stand: 8, scoreHits: 6, scoreGroups: 2, scoreTargets: 6, scoreInner: 6 },
];

// Canonical orderings so the grouped rows render in a stable, conventional order.
const ANALYSIS_WEAPON_ORDER = ['fin', 'grov', 'mil', 'milrp', 'rev', 'revrp', 'sp.pistol', 'sp.rev', 'mag1', 'mag2', 'luft'];
const ANALYSIS_SKILL_CLASS_ORDER = ['A', 'B', 'C', 'D', 'Å'];

// Explicit per-class max entry for a stand, or null. sp.pistol/sp.rev read
// convention.stageStandsSpecial; mag1/mag2 read convention.stageStandsMagnum.
function perClassStandEntry(convention, stand, weaponClass) {
    const n = parseInt(stand, 10);
    let arr = null;
    if (SPECIAL_PISTOL_REV_CLASSES.includes(weaponClass)) arr = convention?.stageStandsSpecial;
    else if (MAGNUM_CLASSES.includes(weaponClass)) arr = convention?.stageStandsMagnum;
    if (!Array.isArray(arr)) return null;
    return arr.find(item => parseInt(item.stand, 10) === n) || null;
}

// Effective per-stand max {scoreHits, scoreTargets, scoreInner} for a weapon class.
//  1) An explicit per-class config (sp/magnum) wins and is used verbatim — the
//     organizer entered the real targets (e.g. 5/2), so no auto-cap.
//  2) Otherwise the default source applies: Norgesfelt's fixed table or felt's
//     convention.stageStands (a felt stand with no entry is "uncapped" → 0, shown
//     as "—"), then capped to 5 for special/magnum (calculateBasedOnClass) — the
//     unchanged fallback. Mirrors getResultsListForAllClasses so Analyse %s match.
export function resolveStandConfig(stage, stand, convention, weaponClass) {
    const currentStand = parseInt(stand, 10);
    const explicit = perClassStandEntry(convention, currentStand, weaponClass);
    if (explicit) {
        return {
            scoreHits: parseInt(explicit.scoreHits, 10) || 0,
            scoreTargets: parseInt(explicit.scoreTargets, 10) || 0,
            scoreInner: parseInt(explicit.scoreInner, 10) || 0,
        };
    }
    let cmv = null;
    if (stage?.type === 'norgesfelt') {
        cmv = NORGESFELT_TARGET_MAX.find(item => item.stand === currentStand) || null;
    } else if (stage?.type === 'felt') {
        cmv = (convention?.stageStands || []).find(item => parseInt(item.stand, 10) === currentStand) || null;
    }
    const feltUncapped = stage?.type === 'felt' && !cmv;
    const cap = (v) => calculateBasedOnClass(resolveWeaponDisplay(weaponClass), v);
    return {
        scoreHits: cap(parseInt(cmv?.scoreHits, 10) || (feltUncapped ? 0 : 6)),
        scoreTargets: cap(parseInt(cmv?.scoreTargets, 10) || (feltUncapped ? 0 : 6)),
        scoreInner: cap(parseInt(cmv?.scoreInner, 10) || (feltUncapped ? 0 : 6)),
    };
}

// Per-stand max for a single cell — thin wrapper over resolveStandConfig kept for
// existing callers (getStandCellInfo, isPerfectSoFar, buildStandAnalysis header).
function standMaxForCell(stage, stand, convention, weaponClass) {
    return resolveStandConfig(stage, stand, convention, weaponClass);
}

// One-decimal percentage, or null when the denominator is 0 (→ rendered as "—").
function analysisPct(sum, max) {
    return max > 0 ? Math.round((sum / max) * 1000) / 10 : null;
}

/**
 * Sums an array of analysis group rows into a single aggregate ("Alle") row with
 * recomputed percentages. Used for both the full per-stand total and the
 * component's total over a filtered subset of groups, so the math lives in one place.
 */
export function sumAnalysisGroups(groups) {
    const total = (groups || []).reduce((acc, g) => {
        acc.n += g.n;
        acc.sumHits += g.sumHits; acc.sumFigures += g.sumFigures; acc.sumInner += g.sumInner;
        acc.maxHits += g.maxHits; acc.maxFigures += g.maxFigures; acc.maxInner += g.maxInner;
        return acc;
    }, { n: 0, sumHits: 0, sumFigures: 0, sumInner: 0, maxHits: 0, maxFigures: 0, maxInner: 0 });
    total.hitPct = analysisPct(total.sumHits, total.maxHits);
    total.figurePct = analysisPct(total.sumFigures, total.maxFigures);
    total.innerPct = analysisPct(total.sumInner, total.maxInner);
    return total;
}

/**
 * Aggregates conventionResult rows into a per-stand breakdown for the Analyse tab.
 * Only stand-based felt stages (felt, norgesfelt) are included. Each stand groups
 * by weapon class × skill class and reports Treff% / Figur% / Inner% (zone =
 * scoreInner) plus raw sum/max, with an "Alle" total per stand. Also returns the
 * distinct weapons and skill classes present, for the filter chips.
 *
 * @returns {{ stageTypes: string[], stands: Array, availableWeapons: Array, availableSkillClasses: string[] }}
 */
export function buildStandAnalysis(convention, conventionResults, participants = [], classMap = [], disciplineMap = []) {
    const empty = { stageTypes: [], stands: [], availableWeapons: [], availableSkillClasses: [] };
    if (!convention || !Array.isArray(convention.stages) || !convention.stages.length) {
        return empty;
    }
    const results = Array.isArray(conventionResults) ? conventionResults : [];

    // md5 → { stage, idx } for stand-based stages only.
    const stageByMd5 = new Map();
    convention.stages.forEach((stage, idx) => {
        if (STAND_BASED_STAGE_TYPES.includes(stage.type)) {
            stageByMd5.set(stage.md5, { stage, idx });
        }
    });
    if (stageByMd5.size === 0) {
        return empty;
    }

    // standKey `${stageType}::${stand}` → bucket { stageType, stand, stage, cellMax, groups: Map }
    // Results from multiple lags that share the same stand number merge into one
    // per-stand bucket on purpose: the Analyse tab reports convention-wide per-stand
    // stats, matching how the Resultatliste merges all felt participants across lags.
    const standMap = new Map();

    results.forEach(result => {
        if (!result) return;
        const entry = stageByMd5.get(result.stage);
        if (!entry) return;
        const { stage, idx } = entry;

        const stand = parseInt(result.stand, 10);
        if (!Number.isFinite(stand) || stand <= 0) return;

        const stageParticipants = convention[`participants${idx + 1}`] || [];
        const stageParticipant = stageParticipants.find(p => p.id === result.participant);
        if (!stageParticipant) return; // cannot resolve weapon class → skip row

        const weapon = stageParticipant.class || 'Ingen klasse';
        const globalParticipant = participants.find(p => p.id === result.participant);
        const skillClass = resolveSkillClass(stageParticipant, globalParticipant, stage, classMap, disciplineMap);
        const skillKey = skillClass === null ? '' : skillClass;

        // cellMax is class-independent, so compute it once per stand and reuse it
        // for every group accumulation and the stand header.
        const standKey = `${stage.type}::${stand}`;
        if (!standMap.has(standKey)) {
            standMap.set(standKey, {
                stageType: stage.type,
                stand,
                stage,
                cellMax: standMaxForCell(stage, stand, convention),
                groups: new Map(),
            });
        }
        const bucket = standMap.get(standKey);

        const weaponLabel = resolveWeaponDisplay(weapon);
        const groupKey = `${weapon}|${skillKey}`;
        if (!bucket.groups.has(groupKey)) {
            bucket.groups.set(groupKey, {
                key: groupKey,
                weapon,
                weaponLabel,
                skillClass,
                label: skillClass ? `${weaponLabel} ${skillClass}` : weaponLabel,
                n: 0,
                sumHits: 0, sumFigures: 0, sumInner: 0,
                maxHits: 0, maxFigures: 0, maxInner: 0,
                // Per-class effective max for this stand: a custom sp/magnum config
                // or the default capped at 5. Added once per shooter row below.
                groupMax: resolveStandConfig(stage, stand, convention, weapon),
                // One row per shooter on this stand, kept for the insight
                // breakdown (loss per shooter, failure kind, top vs rest).
                rows: [],
            });
        }
        const group = bucket.groups.get(groupKey);

        const { groupMax } = group;
        const hits = result.scoreHits || 0;
        const figures = result.scoreTargets || 0;
        const inner = result.scoreInner || 0;
        group.n += 1;
        group.sumHits += hits;
        group.sumFigures += figures;
        group.sumInner += inner;
        group.maxHits += groupMax.scoreHits;
        group.maxFigures += groupMax.scoreTargets;
        group.maxInner += groupMax.scoreInner;
        group.rows.push({
            participantId: result.participant,
            stageMd5: stage.md5,
            name: `${stageParticipant.firstname || ''} ${stageParticipant.lastname || ''}`.trim() || 'Ukjent skytter',
            sportsClub: stageParticipant.sportsClub || '',
            hits,
            figures,
            inner,
            maxHits: groupMax.scoreHits,
            maxFigures: groupMax.scoreTargets,
            maxInner: groupMax.scoreInner,
        });
    });

    const stands = Array.from(standMap.values()).map(bucket => {
        const groups = Array.from(bucket.groups.values()).map(g => ({
            ...g,
            hitPct: analysisPct(g.sumHits, g.maxHits),
            figurePct: analysisPct(g.sumFigures, g.maxFigures),
            innerPct: analysisPct(g.sumInner, g.maxInner),
        }));
        groups.sort((a, b) => {
            const wa = ANALYSIS_WEAPON_ORDER.indexOf(a.weapon);
            const wb = ANALYSIS_WEAPON_ORDER.indexOf(b.weapon);
            const wda = wa < 0 ? 999 : wa;
            const wdb = wb < 0 ? 999 : wb;
            if (wda !== wdb) return wda - wdb;
            const sa = ANALYSIS_SKILL_CLASS_ORDER.indexOf(a.skillClass);
            const sb = ANALYSIS_SKILL_CLASS_ORDER.indexOf(b.skillClass);
            return (sa < 0 ? 99 : sa) - (sb < 0 ? 99 : sb);
        });

        return {
            stageType: bucket.stageType,
            stand: bucket.stand,
            standMax: bucket.cellMax,
            info: getStandInfo(convention, bucket.stand),
            groups,
            total: sumAnalysisGroups(groups),
        };
    });

    stands.sort((a, b) => {
        if (a.stageType !== b.stageType) return a.stageType < b.stageType ? -1 : 1;
        return a.stand - b.stand;
    });

    const stageTypes = Array.from(new Set(stands.map(s => s.stageType)));

    // Distinct weapons and skill classes present, in canonical order — these drive
    // the two filter chip rows (Våpen / Klasse). Skill class null (Norgesfelt) is
    // omitted from the skill chips; the "Alle" chip still includes those rows.
    const weaponMap = new Map();
    const skillSet = new Set();
    stands.forEach(s => s.groups.forEach(g => {
        if (!weaponMap.has(g.weapon)) {
            weaponMap.set(g.weapon, { weapon: g.weapon, weaponLabel: g.weaponLabel });
        }
        if (g.skillClass) skillSet.add(g.skillClass);
    }));
    const availableWeapons = Array.from(weaponMap.values()).sort((a, b) => {
        const wa = ANALYSIS_WEAPON_ORDER.indexOf(a.weapon);
        const wb = ANALYSIS_WEAPON_ORDER.indexOf(b.weapon);
        return (wa < 0 ? 999 : wa) - (wb < 0 ? 999 : wb);
    });
    const availableSkillClasses = Array.from(skillSet).sort((a, b) => {
        const sa = ANALYSIS_SKILL_CLASS_ORDER.indexOf(a);
        const sb = ANALYSIS_SKILL_CLASS_ORDER.indexOf(b);
        return (sa < 0 ? 99 : sa) - (sb < 0 ? 99 : sb);
    });

    return { stageTypes, stands, availableWeapons, availableSkillClasses };
}

// Insight helpers below work on the per-shooter rows buildStandAnalysis keeps in
// each group, so the component can hand over only the groups that survive its
// weapon/class filter and every number follows the filter.

const INSIGHT_TOP_LOSERS = 5;
// A top/rest split needs at least this many shooters to say anything.
const INSIGHT_MIN_FIELD = 3;

function rowLoss(row) {
    // A row without a known max (uncapped felt) carries no measurable loss.
    const lossOf = (max, value) => (max > 0 ? Math.max(0, max - value) : 0);
    return {
        lostHits: lossOf(row.maxHits, row.hits),
        lostFigures: lossOf(row.maxFigures, row.figures),
        lostInner: lossOf(row.maxInner, row.inner),
    };
}

function fieldKey(row) {
    return `${row.stageMd5}::${row.participantId}`;
}

/**
 * Per-stand insight on top of buildStandAnalysis: where the field loses its
 * points, what kind of failure it is, and who fails there.
 *
 * @param {Array<{ stand: object, groups: Array }>} entries — a stand from
 *   buildStandAnalysis paired with the groups to include (already filtered).
 * @returns {{
 *   stands: Array, fieldSize: number,
 *   hardest: object|null, mostLoss: object|null, deciding: object|null,
 * }}
 * Each stand reports: n, scored, hitPct, lossHits/lossFigures/lossInner,
 * lossShare (% of all lost hits across the stands), the failure-kind counts
 * and % (clean, droppedHits, missedFigure, innerOnly), a histogram of lost
 * hits, topLosers (max 5, worst first) and the top-third vs rest hit % gap.
 */
export function buildStandInsights(entries) {
    const list = Array.isArray(entries) ? entries : [];
    const perStand = list.map(({ stand, groups }) => {
        const rows = (groups || []).flatMap(g => (g.rows || []).map(r => ({ ...r, ...rowLoss(r) })));
        return { stand, rows };
    });

    // Field = every distinct start seen on any of the stands, ranked by total
    // hits over those stands. Top third (rounded up) vs the rest.
    const totals = new Map();
    perStand.forEach(({ rows }) => rows.forEach(r => {
        const key = fieldKey(r);
        totals.set(key, (totals.get(key) || 0) + r.hits);
    }));
    const fieldSize = totals.size;
    const topKeys = new Set();
    if (fieldSize >= INSIGHT_MIN_FIELD) {
        const ranked = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
        ranked.slice(0, Math.ceil(fieldSize / 3)).forEach(([key]) => topKeys.add(key));
    }

    const stands = perStand.map(({ stand, rows }) => {
        const scoredRows = rows.filter(r => r.maxHits > 0);
        const scored = scoredRows.length > 0;
        const sum = (arr, pick) => arr.reduce((acc, r) => acc + pick(r), 0);
        const sumHits = sum(rows, r => r.hits);
        const maxHits = sum(rows, r => r.maxHits);

        let clean = 0; let droppedHits = 0; let missedFigure = 0; let innerOnly = 0;
        scoredRows.forEach(r => {
            if (r.lostFigures > 0) missedFigure += 1;
            else if (r.lostHits > 0) droppedHits += 1;
            else {
                clean += 1;
                if (r.lostInner > 0) innerOnly += 1;
            }
        });
        const pctOf = count => (scored ? analysisPct(count, scoredRows.length) : null);

        const histMap = new Map();
        scoredRows.forEach(r => histMap.set(r.lostHits, (histMap.get(r.lostHits) || 0) + 1));
        const histogram = Array.from(histMap.entries())
            .map(([lost, count]) => ({ lost, count }))
            .sort((a, b) => a.lost - b.lost);

        const topLosers = scoredRows
            .filter(r => r.lostHits > 0 || r.lostFigures > 0)
            .sort((a, b) => (b.lostHits - a.lostHits) || (b.lostFigures - a.lostFigures) || (b.lostInner - a.lostInner))
            .slice(0, INSIGHT_TOP_LOSERS)
            .map(r => ({
                participantId: r.participantId,
                stageMd5: r.stageMd5,
                name: r.name,
                sportsClub: r.sportsClub,
                hits: r.hits,
                maxHits: r.maxHits,
                lostHits: r.lostHits,
                lostFigures: r.lostFigures,
                lostInner: r.lostInner,
            }));

        let topPct = null; let restPct = null; let gap = null;
        if (topKeys.size > 0 && scored) {
            const topRows = scoredRows.filter(r => topKeys.has(fieldKey(r)));
            const restRows = scoredRows.filter(r => !topKeys.has(fieldKey(r)));
            topPct = analysisPct(sum(topRows, r => r.hits), sum(topRows, r => r.maxHits));
            restPct = analysisPct(sum(restRows, r => r.hits), sum(restRows, r => r.maxHits));
            if (topPct != null && restPct != null) gap = Math.round((topPct - restPct) * 10) / 10;
        }

        return {
            key: `${stand.stageType}-${stand.stand}`,
            stageType: stand.stageType,
            stand: stand.stand,
            n: rows.length,
            scored,
            hitPct: analysisPct(sumHits, maxHits),
            lossHits: sum(rows, r => r.lostHits),
            lossFigures: sum(rows, r => r.lostFigures),
            lossInner: sum(rows, r => r.lostInner),
            lossShare: null,
            clean,
            droppedHits,
            missedFigure,
            innerOnly,
            cleanPct: pctOf(clean),
            droppedHitsPct: pctOf(droppedHits),
            missedFigurePct: pctOf(missedFigure),
            innerOnlyPct: pctOf(innerOnly),
            histogram,
            topLosers,
            topPct,
            restPct,
            gap,
        };
    });

    const totalLoss = stands.reduce((acc, s) => acc + s.lossHits, 0);
    stands.forEach(s => {
        s.lossShare = totalLoss > 0 ? analysisPct(s.lossHits, totalLoss) : (s.scored ? 0 : null);
    });

    const scoredStands = stands.filter(s => s.scored && s.hitPct != null);
    const pickBy = (arr, better) => arr.reduce((best, s) => (best === null || better(s, best) ? s : best), null);
    const hardest = pickBy(scoredStands, (s, best) => s.hitPct < best.hitPct);
    const withLoss = scoredStands.filter(s => s.lossHits > 0);
    const mostLoss = pickBy(withLoss, (s, best) => s.lossHits > best.lossHits);
    const withGap = scoredStands.filter(s => s.gap != null);
    const deciding = pickBy(withGap, (s, best) => s.gap > best.gap);

    return { stands, fieldSize, hardest, mostLoss, deciding };
}

// ---------------------------------------------------------------------------
// Lagresultater (score teams): free-form teams of 2-4 shooters assembled by an
// admin and ranked by summed individual totals within a manually chosen
// category. One shooter belongs to at most one team.
// ---------------------------------------------------------------------------

export const SCORE_TEAM_MIN_MEMBERS = 2;
export const SCORE_TEAM_MAX_MEMBERS = 4;

// Category codes align with the individual result list's age/gender buckets
// (AGE_ORDER in resultsTable.js) so sections sort in the same familiar order.
export const SCORE_TEAM_CATEGORIES = [
    { value: 'open', label: 'Åpen' },
    { value: 'u', label: 'Ungdom (U)' },
    { value: 'jr', label: 'Junior' },
    { value: 'k', label: 'Kvinner' },
    { value: 'm', label: 'Menn' },
    { value: 'v55', label: 'Veteran 55' },
    { value: 'v65', label: 'Veteran 65' },
    { value: 'v73', label: 'Veteran 73' },
];

export function scoreTeamCategoryLabel(value) {
    const cat = SCORE_TEAM_CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : 'Åpen';
}

/**
 * Builds the Lagresultater sections from convention.scoreTeams[] and the bucket
 * map produced by conventionResult.getResultsListForAllClasses. A member is a
 * specific start (participantId + weapon); members stored without a weapon
 * (added before stage assignment) fall back to that shooter's best start.
 * Returns [{ category, categoryLabel, teams }] in canonical category order,
 * with teams ranked by totalScore, then totalIx, then name.
 */
export function buildScoreTeamSections(scoreTeams, resultList) {
    const teams = scoreTeams || [];
    if (!teams.length) return [];

    // participantId+weapon → best entry, plus per-participant best across weapons.
    const byStart = new Map();
    const byParticipant = new Map();
    Object.values(resultList || {}).forEach(bucket => {
        if (bucket.championship) return; // skip duplicate KM/DM buckets
        (bucket.participants || []).forEach(p => {
            const entry = { ...p, weaponClass: bucket.weaponClass, weaponDisplay: bucket.weaponDisplay };
            const startKey = `${bucket.weaponClass}::${p.id}`;
            const bestStart = byStart.get(startKey);
            if (!bestStart || (p.totalScore || 0) > (bestStart.totalScore || 0)) {
                byStart.set(startKey, entry);
            }
            const best = byParticipant.get(p.id);
            if (!best || (p.totalScore || 0) > (best.totalScore || 0)) {
                byParticipant.set(p.id, entry);
            }
        });
    });

    const knownCategory = value => SCORE_TEAM_CATEGORIES.some(c => c.value === value);
    const sections = {};
    teams.forEach(team => {
        const category = knownCategory(team.category) ? team.category : 'open';
        if (!sections[category]) {
            sections[category] = { category, categoryLabel: scoreTeamCategoryLabel(category), teams: [] };
        }
        const members = (team.members || []).map(m => {
            const entry = m.weapon
                ? byStart.get(`${m.weapon}::${m.participantId}`)
                : byParticipant.get(m.participantId);
            return entry ? { ...entry } : {
                id: m.participantId,
                name: m.name || `#${m.participantId}`,
                sportsClub: m.sportsClub || '',
                weaponClass: m.weapon || '',
                weaponDisplay: '',
                totalScore: 0,
                totalIx: 0,
                missing: true,
            };
        });
        const totalScore = members.reduce((sum, m) => sum + (m.totalScore || 0), 0);
        const totalIx = members.reduce((sum, m) => sum + (m.totalIx || 0), 0);
        const missing = members.filter(m => m.missing).length;
        const clubs = Array.from(new Set(members.map(m => m.sportsClub).filter(Boolean)));
        sections[category].teams.push({
            md5: team.md5,
            name: team.name || '(uten navn)',
            club: clubs.length === 1 ? clubs[0] : clubs.join(' / '),
            category,
            members,
            totalScore,
            totalIx,
            missing,
            complete: members.length >= SCORE_TEAM_MIN_MEMBERS && missing === 0,
        });
    });

    Object.values(sections).forEach(sec => {
        sec.teams.sort((a, b) => {
            if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore;
            if (b.totalIx !== a.totalIx) return b.totalIx - a.totalIx;
            return (a.name || '').localeCompare(b.name || '');
        });
    });
    const categoryRank = (c) => {
        const i = SCORE_TEAM_CATEGORIES.findIndex(x => x.value === c);
        return i < 0 ? 99 : i;
    };
    return Object.values(sections).sort((a, b) => categoryRank(a.category) - categoryRank(b.category));
}

// Normalize a phone number for comparison: keep a leading +, strip everything
// else that isn't a digit ("+47 915 20 191" → "+4791520191").
function normalizeCellphone(phone) {
    if (!phone) return '';
    return String(phone).trim().replace(/^(\+)?(.*)$/, (match, plus, rest) => (plus || '') + rest.replace(/\D/g, ''));
}

// Shared forgiving matcher for the convention people lists (Medlemmer /
// Arrangører): entry.id refers to user.id but can be stored as a string
// (manual entry in the array-object form), and a hand-typed entry may carry
// only a cellphone or email — so match on numeric id, normalized cellphone,
// or case-insensitive email.
function userMatchesPeopleList(list, user) {
    if (!Array.isArray(list) || list.length === 0 || !user) {
        return false;
    }
    const userId = parseInt(user.id, 10) || 0;
    const userPhone = normalizeCellphone(user.cellphone);
    const userEmail = (user.email || '').trim().toLowerCase();

    return list.some(m => {
        if (!m) {
            return false;
        }
        const memberId = parseInt(m.id, 10) || 0;
        if (memberId > 0 && memberId === userId) {
            return true;
        }
        const memberPhone = normalizeCellphone(m.cellphone);
        if (memberPhone && memberPhone === userPhone) {
            return true;
        }
        const memberEmail = (m.email || '').trim().toLowerCase();
        return !!(memberEmail && memberEmail === userEmail);
    });
}

// True when the logged-in user is on the convention's "Medlemmer" list (edit-
// convention drawer). Members are the per-convention scoring crew, so this
// check grants score entry alongside system/customer admin.
export function isConventionMember(convention, user) {
    if (!convention || !user) {
        return false;
    }
    return userMatchesPeopleList(convention.members, user);
}

// True when the logged-in user is on the convention's "Arrangører" list. The
// organizer crew (standplassledere etc.) gets score entry too — mirrors the
// organizers branch of isConventionOfficial in backend/lib/convention-access.js.
export function isConventionOrganizer(convention, user) {
    if (!convention || !user) {
        return false;
    }
    return userMatchesPeopleList(convention.organizers, user);
}

// Parse Rubic's competition-result rejection body into human-readable rows so the UI can show
// exactly WHY a report failed (e.g. "Even Holthe — Person is not member in organization").
// On a 400 Rubic returns an array of { rowIdentifier, propertyName, errorMessage, attemptedValue };
// rowIdentifier embeds the personId (= our user.rubicId), which we map back to a shooter name/club
// via the resolved result rows the backend returned. Rows are deduped per person+property+message
// (Rubic emits one row per discipline, so one bad shooter otherwise appears many times).
// Returns { status, statusText, rows: [{ name, club, property, message, personId }], raw }.
export function parseRubicReportErrors(rubicResponse, resultsWithNames = []) {
    if (!rubicResponse || typeof rubicResponse !== 'object') {
        return { status: null, statusText: '', rows: [], raw: '' };
    }
    const raw = typeof rubicResponse.data === 'string' ? rubicResponse.data : '';
    const byRubicId = {};
    (Array.isArray(resultsWithNames) ? resultsWithNames : []).forEach(r => {
        if (r && r.rubicId) {
            byRubicId[r.rubicId] = r;
        }
    });

    let parsed = null;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        parsed = null;
    }

    const rows = [];
    if (Array.isArray(parsed)) {
        const seen = new Set();
        parsed.forEach(err => {
            if (!err || typeof err !== 'object') {
                return;
            }
            const match = /personId:\s*([0-9a-fA-F-]+)/.exec(err.rowIdentifier || '');
            const personId = match ? match[1] : '';
            const person = personId ? byRubicId[personId] : null;
            const property = err.propertyName || '';
            const message = err.errorMessage || '';
            const key = `${personId}|${property}|${message}`;
            if (seen.has(key)) {
                return;
            }
            seen.add(key);
            rows.push({
                name: person ? person.name : '',
                club: person ? person.club : '',
                property,
                message,
                personId,
            });
        });
    }

    return {
        status: rubicResponse.status != null ? rubicResponse.status : null,
        statusText: rubicResponse.statusText || '',
        rows,
        raw,
    };
}
